import {
  Activity,
  BookOpen,
  Bot,
  Brain,
  Contact,
  Database,
  FileText,
  FolderOpen,
  Lock,
  Mail,
  Map,
  Network,
  Paintbrush,
  Palette,
  Rocket,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  SquareKanban,
  Terminal,
  Users,
  Users2,
  Workflow,
  Box,
  type LucideIcon,
} from 'lucide-react';

/**
 * Atlas module nodes carry only the icon *name* (string) in metadata —
 * we can't serialize React components into the DB. This map resolves
 * the name back to a lucide-react component at render time.
 *
 * Adding a new module: pick a lucide name, register it here, set it on
 * the atlas node's metadata.iconName. If the name isn't registered we
 * fall back to a neutral box so the dashboard never throws.
 *
 * ── AND AN ENTRY LEAVES WITH THE LAST NAME THAT ASKED FOR IT ────────────────
 * `CalendarRange` and `Inbox` were registered ONLY because `coreWindowablePages`'
 * `dm-sprints` / `dm-inbox` entries named them as `pluginIcon`. gh#2607 PR-5 retired
 * both launcher entries, so both icons were dropped in the same change: an unreferenced
 * member of this map is not inert — it reads as evidence that some surface still wants
 * it, which is how a retirement leaves a trail nobody can follow back.
 *
 * BOTH, not one. Their justifications were the same sentence with a different slug in
 * it, so keeping either would have been the sibling this map could not tell apart
 * ([[feedback_sibling_defensive_arms_equal_treatment]]). Measured before removal: no
 * source names them as an ICON NAME and no `platform_atlas_nodes.metadata` row carries
 * one. A future surface re-registers the name in the same commit that uses it.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  // Registered by DM W2 A1's `dm-activity` launcher entry — the name that asked for it,
  // in the same change that uses it, per the rule above. `Bot` STAYS: the ai module
  // manifest still names it, so it did not leave with the dm entry that also used it.
  Activity,
  Bot,
  Brain,
  Contact,
  Database,
  FileText,
  FolderOpen,
  Lock,
  Mail,
  Map,
  Network,
  Paintbrush,
  Palette,
  Rocket,
  // `dm-policies` launcher entry (W3-B UIa).
  ScrollText,
  // `dm-docs` launcher entry (W3-B D3).
  BookOpen,
  // `coreWindowablePages` settings entry (gh#2502/gh#2549).
  Settings,
  Shield,
  // `security` module manifest (sidebar + dashboard tile).
  ShieldCheck,
  SquareKanban,
  Terminal,
  Users,
  Users2,
  Workflow,
};

export function resolveModuleIcon(name: string | undefined | null): LucideIcon {
  if (!name) return Box;
  return ICON_MAP[name] ?? Box;
}

interface Props {
  name: string | undefined | null;
  className?: string;
}

export function ModuleIcon({ name, className }: Props) {
  const Icon = resolveModuleIcon(name);
  return <Icon className={className} />;
}
